import { TableCellType, TableColumns } from '@teamsnap/snap-ui';
import { ActionColumn } from 'components/common/action-column';
import { TruncateWithPopover } from 'components/common/truncate';
import { OrderLineItem } from 'components/pages/order-details';
import { currencyGivenPennies } from 'core/utils';
import * as React from 'react';
import { ReactNode } from 'react';

const feeTypeMap = {
  registration_fee: 'Registration',
  adjustment: 'Adjustment',
  credit: 'Credit',
  fee: 'Addon',
};

// Italicize the column value if the line item is canceled
const renderColumn = (columnValue: any, canceled: boolean) =>
  canceled ? <i>{columnValue}</i> : columnValue;

export const canCancelItem = (lineItem: OrderLineItem) =>
  !lineItem.canceled &&
  (lineItem.type === 'registration_fee' ||
    lineItem.type === 'fee' ||
    (lineItem.type === 'credit' &&
      lineItem.externalType !== 'waitlist_discount'));

export const getColumns = (
  onCancel: (lineItem: OrderLineItem) => void
): TableColumns => ({
  participantName: {
    label: 'Participant',
    align: 'left',
    width: '20%',
    sort: 'alpha',
    getBodyProps: (row: OrderLineItem) => ({
      type: TableCellType.Custom,
      children: (
        <div className="sui-break-all">
          <TruncateWithPopover
            text={row.participantName || ''}
            maxLength={30}
          />
        </div>
      ),
    }),
  },
  participantGroupName: {
    label: 'Participant Group',
    align: 'left',
    width: '20%',
    sort: 'alpha',
    getBodyProps: (row: OrderLineItem) => ({
      type: TableCellType.Custom,
      children: renderColumn(row.participantGroupName, row.canceled),
    }),
  },
  type: {
    label: 'Type',
    align: 'left',
    width: '120px',
    sort: 'alpha',
    getBodyProps: (row: OrderLineItem) => {
      const { externalType, canceled } = row;
      const rowType = ['coupon_code_discount', 'automatic_discount'].includes(
        externalType
      )
        ? 'Discount'
        : feeTypeMap[row.type];
      return {
        type: TableCellType.Custom,
        children: renderColumn(rowType, canceled),
      };
    },
  },
  description: {
    label: 'Description',
    align: 'left',
    width: '20%',
    sort: 'alpha',
    getBodyProps: (row: OrderLineItem) => ({
      type: TableCellType.Custom,
      children: renderColumn(row.description, row.canceled),
    }),
  },
  amount: {
    label: 'Amount',
    align: 'left',
    width: '20%',
    sort: 'numeric',
    getBodyProps: (row: OrderLineItem) => {
      const { amount } = row;
      let children: ReactNode;
      if (amount && amount < 0) {
        children = (
          <div>
            <span className="u=colorNegative">
              {currencyGivenPennies(amount)}
            </span>
            {row.canceled ? ' (Canceled)' : ''}
          </div>
        );
      } else {
        children = `${currencyGivenPennies(amount)} ${
          row.canceled ? ' (Canceled)' : ''
        }`;
      }
      return {
        type: TableCellType.Custom,
        children,
      };
    },
  },
  actions: {
    label: '',
    align: 'center',
    width: '5%',
    getBodyProps: (row: OrderLineItem) => ({
      type: TableCellType.Custom,
      children: canCancelItem(row) ? (
        <ActionColumn
          testId="fees-table--actions"
          actions={[
            {
              label: 'Cancel',
              onClick: () => onCancel(row),
              disabled: !canCancelItem(row),
            },
          ]}
        />
      ) : null,
    }),
  },
});
